import React from 'react';
import { X } from 'lucide-react';
import { MAX_FOLDER_NAME_LENGTH, folderNameTooLongMessage } from '@/constants/folderName';
import type { OfferingVorlageFolder } from '../types';
import type { MaterialFolder } from '../types/material';
import type { LeistungFolder } from '../types/leistung';
import type { StammdatenTab } from '../../../utils/stammdatenRoutes';
import { parseApiJsonResponse } from '../utils/materialApi';
import { folderDeleteItemLabel, truncateFolderNameLabel } from '../utils/folderName';

const MAX_ORDNER_ROWS = 10;

export type StammdatenFolderDeleteTarget =
  | { kind: 'material'; folder: MaterialFolder }
  | { kind: 'leistung'; folder: LeistungFolder }
  | { kind: 'vorlage'; folder: OfferingVorlageFolder };

type UseStammdatenFolderModalsParams = {
  onMaterialFolderCreated: (folderId: string) => void;
  onLeistungFolderCreated: (folderId: string) => void;
  onVorlageFolderCreated: (folderId: string) => void;
  onMaterialFolderDeleted: (folderId: string) => void;
  onLeistungFolderDeleted: (folderId: string) => void;
  onVorlageFolderDeleted: (folderId: string) => void;
  refreshMaterialFolders: () => Promise<void>;
  refreshLeistungFolders: () => Promise<void>;
  refreshVorlageFolders: () => Promise<void>;
};

export function useStammdatenFolderModals({
  onMaterialFolderCreated,
  onLeistungFolderCreated,
  onVorlageFolderCreated,
  onMaterialFolderDeleted,
  onLeistungFolderDeleted,
  onVorlageFolderDeleted,
  refreshMaterialFolders,
  refreshLeistungFolders,
  refreshVorlageFolders,
}: UseStammdatenFolderModalsParams) {
  const [ordnerModalContext, setOrdnerModalContext] = React.useState<StammdatenTab>('materialien');
  const [ordnerModalOpen, setOrdnerModalOpen] = React.useState(false);
  const [ordnerSlotNames, setOrdnerSlotNames] = React.useState<string[]>(['']);
  const [ordnerModalError, setOrdnerModalError] = React.useState('');
  const [folderSaving, setFolderSaving] = React.useState(false);
  const [folderDeleteConfirm, setFolderDeleteConfirm] = React.useState<StammdatenFolderDeleteTarget | null>(null);
  const [folderDeleteError, setFolderDeleteError] = React.useState<string | null>(null);
  const [folderDeleteSaving, setFolderDeleteSaving] = React.useState(false);

  const openOrdnerModal = React.useCallback((context: StammdatenTab) => {
    setOrdnerModalContext(context);
    setOrdnerModalOpen(true);
    setOrdnerSlotNames(['']);
    setOrdnerModalError('');
  }, []);

  const closeOrdnerModal = React.useCallback(() => {
    setOrdnerModalOpen(false);
    setOrdnerSlotNames(['']);
    setOrdnerModalError('');
  }, []);

  const requestFolderDelete = React.useCallback((target: StammdatenFolderDeleteTarget) => {
    setFolderDeleteConfirm(target);
  }, []);

  const addOrdnerRow = () => {
    setOrdnerSlotNames((prev) => (prev.length < MAX_ORDNER_ROWS ? [...prev, ''] : prev));
  };

  const removeOrdnerRow = (index: number) => {
    setOrdnerSlotNames((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateOrdnerSlot = (index: number, value: string) => {
    const v = value.slice(0, MAX_FOLDER_NAME_LENGTH);
    setOrdnerSlotNames((prev) => prev.map((s, i) => (i === index ? v : s)));
  };

  const submitOrdnerSlots = async () => {
    const names = ordnerSlotNames.map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) {
      setOrdnerModalError('Mindestens einen Ordnernamen eintragen.');
      return;
    }
    const tooLong = names.find((n) => n.length > MAX_FOLDER_NAME_LENGTH);
    if (tooLong) {
      setOrdnerModalError(folderNameTooLongMessage);
      return;
    }
    setFolderSaving(true);
    setOrdnerModalError('');
    let lastId: string | null = null;
    const ctx = ordnerModalContext;
    const isMaterial = ctx === 'materialien';
    const isVorlage = ctx === 'vorlagen';
    try {
      for (const name of names) {
        const url = isMaterial
          ? '/api/material-folders'
          : isVorlage
            ? '/api/offering-vorlage-folders'
            : '/api/leistung-folders';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isMaterial ? { name, section: 'ordner' } : { name }),
        });
        const d = (await parseApiJsonResponse(res)) as { error?: string; id?: string };
        if (!res.ok) throw new Error(d?.error || 'Ordner konnte nicht angelegt werden.');
        if (d?.id) lastId = d.id;
      }
      if (isMaterial) {
        await refreshMaterialFolders();
        if (lastId) onMaterialFolderCreated(lastId);
      } else if (isVorlage) {
        await refreshVorlageFolders();
        if (lastId) onVorlageFolderCreated(lastId);
      } else {
        await refreshLeistungFolders();
        if (lastId) onLeistungFolderCreated(lastId);
      }
      closeOrdnerModal();
    } catch (err: unknown) {
      setOrdnerModalError(err instanceof Error ? err.message : 'Ordner konnten nicht angelegt werden.');
    } finally {
      setFolderSaving(false);
    }
  };

  const closeFolderDeleteConfirm = () => {
    if (!folderDeleteSaving) setFolderDeleteConfirm(null);
  };

  const runFolderDelete = () => {
    const pending = folderDeleteConfirm;
    if (!pending) return;
    setFolderDeleteSaving(true);
    setFolderDeleteError(null);
    const url =
      pending.kind === 'material'
        ? `/api/material-folders/${encodeURIComponent(pending.folder.id)}`
        : pending.kind === 'vorlage'
          ? `/api/offering-vorlage-folders/${encodeURIComponent(pending.folder.id)}`
          : `/api/leistung-folders/${encodeURIComponent(pending.folder.id)}`;
    fetch(url, { method: 'DELETE' })
      .then(async (r) => {
        const d = (await parseApiJsonResponse(r)) as { error?: string; success?: boolean };
        if (!r.ok) throw new Error(d?.error || 'Ordner konnte nicht gelöscht werden.');
        return d;
      })
      .then(() => {
        if (pending.kind === 'material') onMaterialFolderDeleted(pending.folder.id);
        else if (pending.kind === 'vorlage') onVorlageFolderDeleted(pending.folder.id);
        else onLeistungFolderDeleted(pending.folder.id);
        setFolderDeleteConfirm(null);
      })
      .catch((err) => {
        setFolderDeleteConfirm(null);
        setFolderDeleteError(err instanceof Error ? err.message : 'Ordner konnte nicht gelöscht werden.');
      })
      .finally(() => setFolderDeleteSaving(false));
  };

  const folderModals = (
    <>
      {ordnerModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => !folderSaving && closeOrdnerModal()}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ordner-modal-title"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="ordner-modal-title" className="text-base font-semibold text-app-text">
                Ordner hinzufügen
              </h2>
              <button
                type="button"
                onClick={() => !folderSaving && closeOrdnerModal()}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                title="Schließen"
                aria-label="Schließen"
                disabled={folderSaving}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {ordnerModalError ? (
              <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {ordnerModalError}
              </p>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (folderSaving) return;
                void submitOrdnerSlots();
              }}
            >
              <div className="max-h-[min(50vh,320px)] space-y-3 overflow-y-auto pr-1">
                {ordnerSlotNames.map((slotName, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      value={slotName}
                      onChange={(e) => updateOrdnerSlot(idx, e.target.value)}
                      disabled={folderSaving}
                      maxLength={MAX_FOLDER_NAME_LENGTH}
                      className="min-w-0 flex-1 rounded-lg border border-app-border px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                      placeholder="Ordnername"
                      autoComplete="off"
                      autoFocus={idx === 0}
                    />
                    {ordnerSlotNames.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeOrdnerRow(idx)}
                        disabled={folderSaving}
                        className="shrink-0 rounded p-1.5 text-app-text-subtle hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        aria-label="Zeile entfernen"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-app-text-subtle">Max. {MAX_FOLDER_NAME_LENGTH} Zeichen pro Name.</p>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={addOrdnerRow}
                    disabled={folderSaving || ordnerSlotNames.length >= MAX_ORDNER_ROWS}
                    className="text-sm font-medium text-pipedrive-blue hover:text-blue-700 hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
                  >
                    Weiterer Ordner
                  </button>
                  {ordnerSlotNames.length >= MAX_ORDNER_ROWS && (
                    <p className="mt-1 text-xs text-app-text-subtle">Maximal {MAX_ORDNER_ROWS} Ordner auf einmal.</p>
                  )}
                </div>
                <div className="flex shrink-0 justify-end gap-2 sm:ml-4">
                  <button type="button" disabled={folderSaving} onClick={closeOrdnerModal} className="btn-pipedrive-secondary">
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={folderSaving || ordnerSlotNames.every((s) => !s.trim())}
                    className="inline-flex items-center justify-center rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {folderSaving ? 'Speichern…' : 'Hinzufügen'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {folderDeleteConfirm && (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/40"
          onClick={() => !folderDeleteSaving && closeFolderDeleteConfirm()}
          role="presentation"
        >
          <div
            className="relative w-full max-w-md rounded-xl border border-app-border bg-app-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="folder-delete-title"
          >
            <button
              type="button"
              onClick={closeFolderDeleteConfirm}
              disabled={folderDeleteSaving}
              className="absolute right-3 top-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
              title="Schließen"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center gap-5 px-2 text-center">
              <h2 id="folder-delete-title" className="text-base font-semibold text-app-text">
                Ordner löschen
              </h2>
              <p className="text-sm leading-relaxed text-app-text-muted">
                Ordner „{truncateFolderNameLabel(folderDeleteConfirm.folder.name)}“ wirklich löschen? Das ist nur
                möglich, wenn dem Ordner keine {folderDeleteItemLabel(folderDeleteConfirm.kind)} zugeordnet sind.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  disabled={folderDeleteSaving}
                  onClick={closeFolderDeleteConfirm}
                  className="btn-pipedrive-secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  disabled={folderDeleteSaving}
                  onClick={() => runFolderDelete()}
                  className="app-btn-danger-outline inline-flex items-center justify-center rounded-lg border border-red-200 bg-app-surface px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {folderDeleteSaving ? 'Löschen…' : 'Löschen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {folderDeleteError && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
          onClick={() => setFolderDeleteError(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="folder-delete-err-title"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="folder-delete-err-title" className="text-base font-semibold text-app-text">
                Hinweis
              </h2>
              <button
                type="button"
                onClick={() => setFolderDeleteError(null)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
                title="Schließen"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-app-text-muted leading-relaxed">{folderDeleteError}</p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setFolderDeleteError(null)}
                className="inline-flex items-center justify-center rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return { openOrdnerModal, requestFolderDelete, folderModals };
}
